import { memo, type CSSProperties, type ReactNode } from "react";

import type { Square } from "chess.js";
import { Chessboard } from "react-chessboard";

type BoardOrientation = "white" | "black";

type ChessSurfaceProps = {
  id: string;
  boardWidth: number;
  position: string;
  orientation?: BoardOrientation;
  arePiecesDraggable?: boolean;
  onPieceDrop?: (sourceSquare: Square, targetSquare?: Square) => boolean;
  onPieceDragBegin?: (piece: string, sourceSquare: Square) => void;
  onPieceDragEnd?: (piece: string, sourceSquare: Square, targetSquare?: Square) => void;
  onSquareClick?: (square: Square) => void;
  customSquareStyles?: Record<string, CSSProperties>;
  leftBoardSlot?: ReactNode;
  lightSquareColor: string;
  darkSquareColor: string;
};

function filesForOrientation(orientation: BoardOrientation) {
  return orientation === "white"
    ? ["a", "b", "c", "d", "e", "f", "g", "h"]
    : ["h", "g", "f", "e", "d", "c", "b", "a"];
}

function ranksForOrientation(orientation: BoardOrientation) {
  return orientation === "white"
    ? ["8", "7", "6", "5", "4", "3", "2", "1"]
    : ["1", "2", "3", "4", "5", "6", "7", "8"];
}

function ChessSurfaceView({
  id,
  boardWidth,
  position,
  orientation = "white",
  arePiecesDraggable = false,
  onPieceDrop,
  onPieceDragBegin,
  onPieceDragEnd,
  onSquareClick,
  customSquareStyles,
  leftBoardSlot,
  lightSquareColor,
  darkSquareColor,
}: ChessSurfaceProps) {
  const files = filesForOrientation(orientation);
  const ranks = ranksForOrientation(orientation);
  const surfaceClassName = leftBoardSlot
    ? "chess-surface chess-surface--with-left-slot"
    : "chess-surface";

  return (
    <div className={surfaceClassName}>
      <div className="chess-surface__ranks" aria-hidden="true">
        {ranks.map((rank) => (
          <span key={rank}>{rank}</span>
        ))}
      </div>

      {leftBoardSlot ? (
        <div className="chess-surface__side-slot">{leftBoardSlot}</div>
      ) : null}

      <div className="chess-surface__board">
        <Chessboard
          id={id}
          boardWidth={boardWidth}
          position={position}
          boardOrientation={orientation}
          arePiecesDraggable={arePiecesDraggable}
          onPieceDrop={onPieceDrop}
          onPieceDragBegin={onPieceDragBegin}
          onPieceDragEnd={onPieceDragEnd}
          onSquareClick={onSquareClick}
          customSquareStyles={customSquareStyles}
          customDarkSquareStyle={{ backgroundColor: darkSquareColor }}
          customLightSquareStyle={{ backgroundColor: lightSquareColor }}
          customBoardStyle={{
            borderRadius: "0",
            boxShadow: "0 0 0 1px rgba(243, 238, 229, 0.12)",
          }}
          showBoardNotation={false}
          animationDuration={160}
        />
      </div>

      <div className="chess-surface__files" aria-hidden="true">
        {files.map((file) => (
          <span key={file}>{file}</span>
        ))}
      </div>
    </div>
  );
}

export const ChessSurface = memo(ChessSurfaceView);
